import React from 'react';
import styled, { css } from 'styled-components/macro';
import { themeGet } from '@styled-system/theme-get';
// @ts-ignore, there is no type
import { props as shouldFprops } from '@styled-system/should-forward-prop';
import {
  typography,
  color,
  space,
  compose,
  position,
  layout,
  variant,
  flexbox,
} from 'styled-system';

import {
  themeGetAsRem,
  themeGetResponsive,
  createStylesVariants,
  trackingId,
  useResponsiveCtx,
} from '@blueprint/ui';
import { _BRPConflictProps } from '../utils.contants';

const styles = createStylesVariants({
  /* Styles applied to the root element if `variant="srOnly"`. Only accessible to screen readers. */
  srOnly: {
    position: 'absolute',
    height: 1,
    width: 1,
    overflow: 'hidden',
  },
  /* Styles applied to the root element if `align="left"`. */
  alignLeft: {
    textAlign: 'left',
  },
  /* Styles applied to the root element if `align="center"`. */
  alignCenter: {
    textAlign: 'center',
  },
  /* Styles applied to the root element if `align="right"`. */
  alignRight: {
    textAlign: 'right',
  },
  /* Styles applied to the root element if `align="justify"`. */
  alignJustify: {
    textAlign: 'justify',
  },
  /* Styles applied to the root element if `nowrap={true}`. */
  noWrap: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  displayBlock: {
    display: 'block',
  },
  /* Styles applied to the root element if `gutterBottom={true}`. */
  gutterBottom: {
    marginBottom: '0.35em',
  },
  /* Styles applied to the root element if `paragraph={true}`. */
  paragraph: {
    marginBottom: 16,
  },
});

// define the variants
const variants = {
  size: variant({
    scale: 'typography.variantMapping',
  }),
  mode: variant({
    prop: 'mode',
    scale: 'typography.modeMapping',
  }),
  font: variant({
    prop: 'fontWeights',
    scale: 'fontWeights',
  }),
};

/**
 * Text
 * @ignore - do not document.
 */
const Text = styled('span').withConfig({
  shouldForwardProp: (prop) =>
    ![...shouldFprops, ..._BRPConflictProps].includes(prop),
})`
  font-family: ${themeGet(
    'fonts.serif',
    'UniversNextforHSBC, Arial, sans-serif',
  )};
  margin: 0;
  padding: 0;
  text-align: left;
  ${({ variant }) => themeGetAsRem(`typography.variantMapping.${variant}`)};

  ${({ hMedia }) => css`
    ${themeGetResponsive({
      hMedia,
      hBreakpoints: 'extraLarge',
      mobileFirst: true,
      hMediaStyles: css`
        ${({ variant }) =>
          themeGetAsRem(`typography.ts3.variantMapping.${variant}`)};
      `,
    })};
    ${themeGetResponsive({
      hMedia,
      hBreakpoints: 'extraLarge',
      hMediaStyles: css`
        ${({ variant }) =>
          themeGetAsRem(`typography.ts2.variantMapping.${variant}`)};
      `,
    })};
    ${themeGetResponsive({
      hMedia,
      hMediaStyles: css`
        ${({ variant }) =>
          themeGetAsRem(`typography.ts1.variantMapping.${variant}`)};
      `,
    })};
  `};

  ${compose(typography, space, position, layout, variants.mode, flexbox)}
  ${compose(color)}
  ${styles}

  ${({ float }) => (float ? `float: ${float};` : '')}
`;

Text.displayName = 'Text';

Text.defaultProps = {
  mode: 'light',
  variant: 'body1',
  'data-testid': 'Text',
  'data-trackid': trackingId('Text'),
};

export default Text;

export const ResponsiveText = (props) => {
  const { hMedia } = useResponsiveCtx();

  return <Text hMedia={hMedia} {...props} />;
};
